import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Input, PhoneInput } from 'components/FormFields';
import Toaster from 'components/Toaster';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #fa4616;
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Override Input component styles for dark theme */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Ensure dark theme overrides apply to inputs in GridRow too */
  label {
    color: #94a3b8 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
  }

  input,
  textarea,
  select {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;

    &::placeholder {
      color: #64748b !important;
    }

    &:focus {
      border-color: #fa4616 !important;
      box-shadow: 0 0 0 1px #fa4616 !important;
    }
  }

  /* Description text styling */
  p {
    color: #94a3b8 !important;
    font-size: 12px !important;
  }
`;

const PromoterTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BusinessTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PromoterTypeTile = styled.div`
  background: ${(props) =>
    props.selected ? 'rgba(250, 70, 22, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  border: 2px solid
    ${(props) => (props.selected ? '#fa4616' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${(props) =>
      props.selected ? '#fa4616' : 'rgba(250, 70, 22, 0.5)'};
    background: ${(props) =>
      props.selected ? 'rgba(250, 70, 22, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const TileTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.selected ? '#fa4616' : '#ffffff')};
  margin: 0 0 8px 0;
`;

const TileDescription = styled.p`
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 12px;
`;

const YesNoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const BusinessDetailsStep = () => {
  const {
    formData,
    updateField,
    updateNestedField,
    errors,
    getTotalSteps,
    getStepNumber,
  } = useSignup();
  const history = useHistory();
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // Auto-populate primary_contact from user's name
  const autoPrimaryContact =
    formData.first_name && formData.last_name
      ? `${formData.first_name} ${formData.last_name}`
      : formData.primary_contact || '';

  // Auto-populate title from user_role if available
  const autoTitle = formData.user_role || formData.title || '';

  const isFormValid =
    formData.promoter_type &&
    formData.business_type &&
    formData.business_dba &&
    formData.legal_name &&
    // Account owner fields required only when not primary owner
    (formData.is_primary_owner !== false ||
      (formData.account_owner?.first_name &&
        formData.account_owner?.last_name &&
        formData.account_owner?.email &&
        formData.account_owner?.cellphone)) &&
    // Payouts contact fields required only when has_separate_payouts_contact
    (!formData.has_separate_payouts_contact ||
      (formData.payouts_contact?.first_name &&
        formData.payouts_contact?.last_name &&
        formData.payouts_contact?.email &&
        formData.payouts_contact?.cellphone));

  const handleContinue = () => {
    if (!isFormValid) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Required Fields Missing',
        message: 'Please fill in all required fields',
      });
      return;
    }

    // Auto-set primary_contact and title before navigating
    if (!formData.primary_contact && autoPrimaryContact) {
      updateField('primary_contact', autoPrimaryContact);
    }
    if (!formData.title && autoTitle) {
      updateField('title', autoTitle);
    }

    // Navigate based on promoter type
    if (formData.promoter_type === 'track') {
      history.push('/promoter-signup/track-address');
    } else {
      history.push('/promoter-signup/shipping-address');
    }
  };

  const handleBack = () => {
    if (formData.is_existing_user) {
      history.push('/promoter-signup/verify');
    } else {
      history.push('/promoter-signup/user-info');
    }
  };

  const handleAccountOwnerField = (field, value) => {
    updateNestedField('account_owner', field, value);
  };

  const handlePayoutsContactField = (field, value) => {
    updateNestedField('payouts_contact', field, value);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/business-details')}
            totalSteps={getTotalSteps()}
            stepTitle="Business Details"
          />

          {/* Business Information Card */}
          <Card style={{ marginBottom: '24px' }}>
            <CardSubtitle>
              Tell us about your business so we can set up your account
              properly.
            </CardSubtitle>

            <SectionTitle>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              Business Information
            </SectionTitle>

            <FormGrid>
              <div>
                <FieldLabel>Promoter Type</FieldLabel>
                <PromoterTypeGrid>
                  <PromoterTypeTile
                    selected={formData.promoter_type === 'track'}
                    onClick={() => updateField('promoter_type', 'track')}
                  >
                    <TileTitle selected={formData.promoter_type === 'track'}>
                      Track Promoter
                    </TileTitle>
                    <TileDescription>
                      You own or operate a racing venue and host events at your
                      facility
                    </TileDescription>
                  </PromoterTypeTile>

                  <PromoterTypeTile
                    selected={formData.promoter_type === 'series'}
                    onClick={() => updateField('promoter_type', 'series')}
                  >
                    <TileTitle selected={formData.promoter_type === 'series'}>
                      Series Promoter
                    </TileTitle>
                    <TileDescription>
                      You organize a racing series or championship across
                      multiple tracks
                    </TileDescription>
                  </PromoterTypeTile>

                  <PromoterTypeTile
                    selected={formData.promoter_type === 'independent'}
                    onClick={() => updateField('promoter_type', 'independent')}
                  >
                    <TileTitle
                      selected={formData.promoter_type === 'independent'}
                    >
                      Independent Promoter
                    </TileTitle>
                    <TileDescription>
                      You promote individual events at various venues without
                      owning a track
                    </TileDescription>
                  </PromoterTypeTile>
                </PromoterTypeGrid>
              </div>

              <div>
                <FieldLabel>Business Type</FieldLabel>
                <BusinessTypeGrid>
                  <PromoterTypeTile
                    selected={formData.business_type === 'individual'}
                    onClick={() => updateField('business_type', 'individual')}
                  >
                    <TileTitle
                      selected={formData.business_type === 'individual'}
                    >
                      Individual
                    </TileTitle>
                    <TileDescription>
                      Operating as an individual or sole proprietor
                    </TileDescription>
                  </PromoterTypeTile>

                  <PromoterTypeTile
                    selected={formData.business_type === 'company'}
                    onClick={() => updateField('business_type', 'company')}
                  >
                    <TileTitle selected={formData.business_type === 'company'}>
                      Company
                    </TileTitle>
                    <TileDescription>
                      Operating as a company, LLC, or corporation
                    </TileDescription>
                  </PromoterTypeTile>
                </BusinessTypeGrid>
              </div>

              <Input
                id="business_dba"
                label="Track or Series Name"
                placeholder="Thunder Valley Motorsports Park"
                value={formData.business_dba || ''}
                onChange={(e) => updateField('business_dba', e.target.value)}
                error={errors.business_dba}
                required
              />

              <Input
                id="legal_name"
                label="Legal Business Entity Name"
                placeholder="Thunder Valley Motorsports, LLC"
                value={formData.legal_name || ''}
                onChange={(e) => updateField('legal_name', e.target.value)}
                error={errors.legal_name}
                required
              />
            </FormGrid>
          </Card>

          {/* Account Owner Card */}
          <Card style={{ marginBottom: '24px' }}>
            <SectionTitle>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Account Owner
            </SectionTitle>

            <FormGrid>
              <div>
                <FieldLabel>Are you the primary Account Owner?</FieldLabel>
                <YesNoGrid>
                  <PromoterTypeTile
                    selected={formData.is_primary_owner === true}
                    onClick={() => updateField('is_primary_owner', true)}
                  >
                    <TileTitle selected={formData.is_primary_owner === true}>
                      Yes
                    </TileTitle>
                    <TileDescription>
                      I am the primary account owner
                    </TileDescription>
                  </PromoterTypeTile>

                  <PromoterTypeTile
                    selected={formData.is_primary_owner === false}
                    onClick={() => updateField('is_primary_owner', false)}
                  >
                    <TileTitle selected={formData.is_primary_owner === false}>
                      No
                    </TileTitle>
                    <TileDescription>
                      Someone else is the primary account owner
                    </TileDescription>
                  </PromoterTypeTile>
                </YesNoGrid>
              </div>

              {formData.is_primary_owner === false && (
                <>
                  <GridRow>
                    <Input
                      id="account_owner_first_name"
                      label="Account Owner First Name"
                      placeholder="Jane"
                      value={formData.account_owner?.first_name || ''}
                      onChange={(e) =>
                        handleAccountOwnerField('first_name', e.target.value)
                      }
                      required
                    />
                    <Input
                      id="account_owner_last_name"
                      label="Account Owner Last Name"
                      placeholder="Smith"
                      value={formData.account_owner?.last_name || ''}
                      onChange={(e) =>
                        handleAccountOwnerField('last_name', e.target.value)
                      }
                      required
                    />
                  </GridRow>
                  <GridRow>
                    <Input
                      id="account_owner_email"
                      label="Account Owner Email"
                      type="email"
                      placeholder="jane@example.com"
                      value={formData.account_owner?.email || ''}
                      onChange={(e) =>
                        handleAccountOwnerField('email', e.target.value)
                      }
                      required
                    />
                    <PhoneInput
                      label="Account Owner Phone"
                      value={formData.account_owner?.cellphone || ''}
                      onChange={(value) =>
                        handleAccountOwnerField('cellphone', value)
                      }
                      countryCode={`+${
                        formData.account_owner?.calling_code || '1'
                      }`}
                      onCountryChange={(code) =>
                        handleAccountOwnerField(
                          'calling_code',
                          code.replace('+', '')
                        )
                      }
                      required
                    />
                  </GridRow>
                </>
              )}
            </FormGrid>
          </Card>

          {/* Payouts Contact Card */}
          <Card>
            <SectionTitle>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Payouts Contact
            </SectionTitle>

            <FormGrid>
              <div>
                <FieldLabel>
                  Would you like to add a separate person to manage your weekly
                  account payouts?
                </FieldLabel>
                <YesNoGrid>
                  <PromoterTypeTile
                    selected={formData.has_separate_payouts_contact === false}
                    onClick={() =>
                      updateField('has_separate_payouts_contact', false)
                    }
                  >
                    <TileTitle
                      selected={formData.has_separate_payouts_contact === false}
                    >
                      No
                    </TileTitle>
                    <TileDescription>
                      Payouts will be managed by the account owner
                    </TileDescription>
                  </PromoterTypeTile>

                  <PromoterTypeTile
                    selected={formData.has_separate_payouts_contact === true}
                    onClick={() =>
                      updateField('has_separate_payouts_contact', true)
                    }
                  >
                    <TileTitle
                      selected={formData.has_separate_payouts_contact === true}
                    >
                      Yes
                    </TileTitle>
                    <TileDescription>
                      A different person handles payouts and finances
                    </TileDescription>
                  </PromoterTypeTile>
                </YesNoGrid>
              </div>

              {formData.has_separate_payouts_contact && (
                <>
                  <GridRow>
                    <Input
                      id="payouts_first_name"
                      label="Payouts Contact First Name"
                      placeholder="Bob"
                      value={formData.payouts_contact?.first_name || ''}
                      onChange={(e) =>
                        handlePayoutsContactField('first_name', e.target.value)
                      }
                      required
                    />
                    <Input
                      id="payouts_last_name"
                      label="Payouts Contact Last Name"
                      placeholder="Johnson"
                      value={formData.payouts_contact?.last_name || ''}
                      onChange={(e) =>
                        handlePayoutsContactField('last_name', e.target.value)
                      }
                      required
                    />
                  </GridRow>
                  <GridRow>
                    <Input
                      id="payouts_email"
                      label="Payouts Contact Email"
                      type="email"
                      placeholder="bob@example.com"
                      value={formData.payouts_contact?.email || ''}
                      onChange={(e) =>
                        handlePayoutsContactField('email', e.target.value)
                      }
                      required
                    />
                    <PhoneInput
                      label="Payouts Contact Phone"
                      value={formData.payouts_contact?.cellphone || ''}
                      onChange={(value) =>
                        handlePayoutsContactField('cellphone', value)
                      }
                      countryCode={`+${
                        formData.payouts_contact?.calling_code || '1'
                      }`}
                      onCountryChange={(code) =>
                        handlePayoutsContactField(
                          'calling_code',
                          code.replace('+', '')
                        )
                      }
                      required
                    />
                  </GridRow>
                </>
              )}
            </FormGrid>

            <ButtonRow>
              <ButtonV2 onClick={handleBack}>← Back</ButtonV2>
              <ButtonV2
                onClick={handleContinue}
                disabled={!isFormValid}
                primary={!!isFormValid}
              >
                Continue →
              </ButtonV2>
            </ButtonRow>
          </Card>
        </ContentWrapper>
      </PageContainer>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ ...toaster, isOpen: false })}
        title={toaster.title}
        message={toaster.message}
        type={toaster.type}
        duration={5000}
      />
    </ThemeProvider>
  );
};

export default BusinessDetailsStep;
